<template>
    <div class="flex flex-col gap-3">
        <div class="flex items-center justify-between">
            <span class="text-sm font-medium">Reminders</span>
            <MuvalButton
                v-if="canModify && entry?.is_scheduled"
                type="button"
                quinary
                icon="system-add-inherit"
                @click.prevent="startCreate"
            >
                Add reminder
            </MuvalButton>
        </div>

        <p
            v-if="!entry?.is_scheduled"
            class="text-xs text-muval-gray-2"
        >
            Give this entry dates before adding a reminder - a reminder is offset from the entry's start.
        </p>

        <div
            v-else-if="loading"
            class="text-xs text-muval-gray-2"
        >
            Loading reminders...
        </div>

        <div
            v-else-if="reminders.length === 0 && !drafting"
            class="text-xs text-muval-gray-2"
        >
            No reminders on this entry.
        </div>

        <div
            v-for="reminder in reminders"
            :key="reminder.id"
            class="flex items-center justify-between gap-3 rounded-md border border-muval-gray-5 px-3 py-2"
        >
            <div class="flex min-w-0 flex-col">
                <span class="text-sm">{{
                    reminder.document_timing ?
                        dayjs
                            .utc(reminder.remind_at)
                            .tz(reminder.document_timing.timezone)
                            .format('D MMM YYYY, h:mm A') +
                        ' (' +
                        reminder.document_timing.timezone +
                        ')'
                    :   formatRemindAt(reminder.remind_at)
                }}</span>
                <span class="truncate text-xs text-muval-gray-2">
                    {{ describeRecipients(reminder) }}
                    <template v-if="reminder.is_sent"> · Sent</template>
                    <template v-if="reminder.paused"> · Choose a future time</template>
                </span>
            </div>
            <MuvalButton
                v-if="canModify && reminder.document_timing && !reminder.is_sent"
                type="button"
                quinary
                small
                @click.prevent="startEdit(reminder)"
                >Edit</MuvalButton
            >
            <button
                v-if="canModify"
                class="rounded-full p-1.5 text-muval-gray-3 hover:bg-muval-gray-6 hover:text-muval-gray-2"
                title="Remove reminder"
                @click.prevent="remove(reminder)"
            >
                <SvgIcon
                    name="system-delete"
                    class="h-4 w-4"
                />
            </button>
        </div>

        <div
            v-if="drafting"
            class="flex flex-col gap-3 rounded-md border border-muval-gray-5 px-3 py-3"
        >
            <!--
                Authored as an offset, stored as a datetime. The API resolves it once
                against the entry's start, so a later reschedule does not silently
                re-aim a reminder somebody set deliberately.
            -->
            <div
                v-if="editingId"
                class="form-titled-item"
            >
                <span class="text-sm font-medium">Reminder date and time ({{ editTimezone }})</span>
                <MuvalInput
                    v-model="editTime"
                    input-type="datetime-local"
                />
            </div>
            <div
                v-else
                class="form-titled-item"
            >
                <span class="text-sm font-medium">Remind</span>
                <MuvalSelect
                    v-model="draft.offset_minutes"
                    :selectOptions="OFFSET_OPTIONS"
                    valueProp="value"
                    displayLabel="label"
                    :canSearch="false"
                    :cannotDeselect="true"
                />
            </div>
            <div class="form-titled-item">
                <span class="text-sm font-medium">People</span>
                <MuvalSelect
                    v-model="draft.recipient_user_ids"
                    mode="multiple"
                    :selectOptions="staffOptions"
                    valueProp="id"
                    displayLabel="name"
                    placeholder="Specific people"
                    :canSearch="true"
                    :multipleLabel="selectedCountLabel"
                />
            </div>
            <div class="form-titled-item">
                <!--
                    A role recipient resolves to that role's members at send time, so
                    somebody who joins next month is reminded and somebody who leaves
                    is not.
                -->
                <span class="text-sm font-medium">Roles</span>
                <MuvalSelect
                    v-model="draft.recipient_roles"
                    mode="multiple"
                    :selectOptions="ROLE_OPTIONS"
                    valueProp="value"
                    displayLabel="label"
                    placeholder="Whole roles"
                    :canSearch="true"
                    :multipleLabel="selectedCountLabel"
                />
            </div>
            <div class="flex justify-end gap-2">
                <MuvalButton
                    type="button"
                    quaternary
                    @click.prevent="drafting = false"
                >
                    Cancel
                </MuvalButton>
                <MuvalButton
                    type="button"
                    :loading="saving"
                    @click.prevent="create"
                >
                    {{ editingId ? 'Save reminder' : 'Add' }}
                </MuvalButton>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs';
import { useForm } from 'vee-validate';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(utc);
dayjs.extend(timezone);
import MuvalInput from '@inputs/MuvalInput.vue';
import companyDiaryApi, { type CompanyDiaryEntry, type CompanyDiaryEntryReminder } from '@/api/companyDiary';
import { useCompanyDiaryStaffOptions } from '@composables/useCompanyDiaryOptions';
import { SALES_ACTION_ROLE_LABELS } from '@composables/useSalesActions';
import { toast } from 'vue-sonner';

/**
 * An entry's reminders (spec company-diary-reminders).
 *
 * The diary is the only surface that creates event reminders in v1 - the table is
 * generic so order-action alerts can consolidate onto it later, but no operational
 * surface grows a reminder feature by accident.
 */

useForm();

const props = defineProps<{
    entry?: CompanyDiaryEntry | null;
    canModify: boolean;
}>();

const editTimezone = ref('UTC');
const editingId = ref<number | null>(null);
const editTime = ref('');
const startEdit = (reminder: CompanyDiaryEntryReminder) => {
    editingId.value = reminder.id;
    editTimezone.value = reminder.document_timing!.timezone;
    editTime.value = dayjs.utc(reminder.remind_at).tz(editTimezone.value).format('YYYY-MM-DDTHH:mm');
    draft.value = {
        ...emptyDraft(),
        recipient_user_ids: [...(reminder.recipient_user_ids || [])],
        recipient_roles: [...(reminder.recipient_roles || [])],
    };
    drafting.value = true;
};

const { staffOptions } = useCompanyDiaryStaffOptions();

/** The offsets an operator actually asks for, in minutes before the entry's start. */
const OFFSET_OPTIONS = [
    { value: 60, label: '1 hour before' },
    { value: 60 * 24, label: '1 day before' },
    { value: 60 * 24 * 2, label: '2 days before' },
    { value: 60 * 24 * 7, label: '1 week before' },
    { value: 60 * 24 * 14, label: '2 weeks before' },
    { value: 60 * 24 * 30, label: '1 month before' },
];

const ROLE_OPTIONS = Object.entries(SALES_ACTION_ROLE_LABELS).map(([value, label]) => ({ value, label }));
const selectedCountLabel = (values: unknown[]) => `${values.length} selected`;

const reminders = ref<CompanyDiaryEntryReminder[]>([]);
const loading = ref(false);
const saving = ref(false);
const drafting = ref(false);

const emptyDraft = () => ({
    offset_minutes: 60 * 24,
    recipient_user_ids: [] as number[],
    recipient_roles: [] as string[],
});

const draft = ref(emptyDraft());

const fetchReminders = async () => {
    if (!props.entry?.id || !props.entry.is_scheduled) {
        reminders.value = [];

        return;
    }

    loading.value = true;
    try {
        const { data } = await companyDiaryApi.indexReminders(props.entry.id);
        reminders.value = data.data ?? [];
    } catch {
        toast.error('Failed to load reminders.');
    } finally {
        loading.value = false;
    }
};

watch(() => [props.entry?.id, props.entry?.is_scheduled], fetchReminders, { immediate: true });

const startCreate = () => {
    editingId.value = null;
    draft.value = emptyDraft();
    drafting.value = true;
};

const create = async () => {
    if (!props.entry?.id) return;

    saving.value = true;
    try {
        /**
         * Empty recipient lists are omitted rather than sent: the API reads "neither
         * key supplied" as "default to the entry's creator", which is the behaviour
         * an operator who did not touch the pickers expects. An explicit empty list
         * would be honoured as "tell nobody".
         */
        const payload: Record<string, unknown> = { offset_minutes: draft.value.offset_minutes };

        if (draft.value.recipient_user_ids.length) {
            payload.recipient_user_ids = draft.value.recipient_user_ids;
        }

        if (draft.value.recipient_roles.length) {
            payload.recipient_roles = draft.value.recipient_roles;
        }

        if (editingId.value) {
            await companyDiaryApi.updateReminder(props.entry.id, editingId.value, {
                remind_at: dayjs.tz(editTime.value, editTimezone.value).utc().format('YYYY-MM-DD HH:mm:ss'),
                recipient_user_ids: draft.value.recipient_user_ids,
                recipient_roles: draft.value.recipient_roles,
            });
        } else {
            await companyDiaryApi.createReminder(props.entry.id, payload);
        }
        drafting.value = false;
        toast.success(editingId.value ? 'Reminder updated.' : 'Reminder added.');
        await fetchReminders();
    } catch (err: any) {
        toast.error(err?.message || 'Failed to add the reminder.');
    } finally {
        saving.value = false;
    }
};

const remove = async (reminder: CompanyDiaryEntryReminder) => {
    if (!props.entry?.id) return;

    try {
        await companyDiaryApi.destroyReminder(props.entry.id, reminder.id);
        toast.success('Reminder removed.');
        await fetchReminders();
    } catch (err: any) {
        toast.error(err?.message || 'Failed to remove the reminder.');
    }
};

const formatRemindAt = (value: string): string => dayjs(value).format('D MMM YYYY, h:mm A');

const describeRecipients = (reminder: CompanyDiaryEntryReminder): string => {
    const people = (reminder.recipient_user_ids ?? [])
        .map((id) => staffOptions.value.find((option) => option.id === id)?.name)
        .filter(Boolean) as string[];

    const roles = (reminder.recipient_roles ?? []).map((role) => SALES_ACTION_ROLE_LABELS[role] ?? role);

    const all = [...people, ...roles];

    return all.length ? all.join(', ') : 'No recipients';
};
</script>
